import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../../MainURL';
import { getAdminSession } from '../../adminSession';
import '../../Admin.scss';

type InvoiceStatus = 'issued' | 'pending' | 'canceled';
type InvoiceType = 'tax_invoice' | 'receipt';

type TaxInvoice = {
  id: number;
  authChurch: string;
  invoiceType: InvoiceType;
  amount: number;
  businessNumber: string;
  issuedAt: string;
  status: InvoiceStatus;
  note: string;
  createdBy: string;
};

type ApiEnvelope<T> = { ok: boolean; data?: T; message?: string };

const TYPE_LABEL: Record<InvoiceType, string> = { tax_invoice: '세금계산서', receipt: '현금영수증' };
const STATUS_LABEL: Record<InvoiceStatus, string> = { issued: '발행 완료', pending: '발행 대기', canceled: '취소됨' };

function errorMessageOf(err: unknown, fallback: string): string {
  const data = axios.isAxiosError(err) ? err.response?.data : null;
  if (data && typeof data === 'object' && 'message' in data) {
    return String((data as { message?: string }).message) || fallback;
  }
  return fallback;
}

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY_FORM = {
  authChurch: '',
  invoiceType: 'tax_invoice' as InvoiceType,
  amount: '',
  businessNumber: '',
  issuedAt: today(),
  note: '',
};

export default function TaxInvoices() {
  const session = getAdminSession();
  const [items, setItems] = useState<TaxInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await axios.get<ApiEnvelope<TaxInvoice[]>>(`${MainURL}/admin-console/tax-invoices`);
      if (res.data?.ok && res.data.data) {
        setItems(res.data.data);
      } else {
        setErrorMsg(res.data?.message || '세금계산서 목록을 불러오지 못했습니다.');
      }
    } catch (err) {
      setErrorMsg(errorMessageOf(err, '세금계산서 목록을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session) return;
    if (!form.authChurch.trim() || !form.issuedAt) {
      alert('교회와 발행일을 입력해 주세요.');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post<ApiEnvelope<null>>(`${MainURL}/admin-console/tax-invoices`, {
        requesterId: session.id,
        ...form,
        amount: Number(form.amount) || 0,
      });
      if (!res.data?.ok) throw new Error(res.data?.message);
      setForm(EMPTY_FORM);
      fetchItems();
    } catch (err) {
      alert(errorMessageOf(err, '등록하지 못했습니다.'));
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (id: number, status: InvoiceStatus) => {
    if (!session) return;
    try {
      const res = await axios.put<ApiEnvelope<null>>(`${MainURL}/admin-console/tax-invoices/${id}/status`, {
        requesterId: session.id,
        status,
      });
      if (!res.data?.ok) throw new Error(res.data?.message);
      fetchItems();
    } catch (err) {
      alert(errorMessageOf(err, '상태를 변경하지 못했습니다.'));
    }
  };

  const handleDelete = async (id: number) => {
    if (!session) return;
    if (!window.confirm('이 항목을 삭제하시겠습니까?')) return;
    try {
      const res = await axios.delete<ApiEnvelope<null>>(`${MainURL}/admin-console/tax-invoices/${id}`, {
        params: { requesterId: session.id },
      });
      if (res.data?.ok) {
        setItems((prev) => prev.filter((item) => item.id !== id));
      } else {
        alert(res.data?.message || '삭제하지 못했습니다.');
      }
    } catch (err) {
      alert(errorMessageOf(err, '삭제하지 못했습니다.'));
    }
  };

  return (
    <div className="Main-cover admin-panel">
      <div className="admin-panel-header">
        <div>
          <h2 className="admin-panel-title">세금계산서 · 영수증</h2>
          <p className="admin-panel-desc">전자세금계산서 발행 및 수납 영수증을 관리합니다.</p>
        </div>
        <button type="button" className="admin-refresh-btn" onClick={fetchItems} disabled={loading}>
          {loading ? '불러오는 중...' : '새로고침'}
        </button>
      </div>

      <form className="admin-form" onSubmit={handleSubmit}>
        <input
          type="text"
          placeholder="authChurch"
          value={form.authChurch}
          onChange={(e) => setForm((prev) => ({ ...prev, authChurch: e.target.value }))}
          style={{ maxWidth: 160 }}
        />
        <select
          value={form.invoiceType}
          onChange={(e) => setForm((prev) => ({ ...prev, invoiceType: e.target.value as InvoiceType }))}
        >
          <option value="tax_invoice">세금계산서</option>
          <option value="receipt">현금영수증</option>
        </select>
        <input
          type="number"
          placeholder="금액"
          value={form.amount}
          onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))}
          style={{ maxWidth: 120 }}
        />
        <input
          type="text"
          placeholder="사업자번호"
          value={form.businessNumber}
          onChange={(e) => setForm((prev) => ({ ...prev, businessNumber: e.target.value }))}
          style={{ maxWidth: 140 }}
        />
        <input
          type="date"
          value={form.issuedAt}
          onChange={(e) => setForm((prev) => ({ ...prev, issuedAt: e.target.value }))}
          style={{ maxWidth: 160 }}
        />
        <input
          type="text"
          placeholder="비고"
          value={form.note}
          onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
        />
        <div className="admin-form-actions">
          <button type="submit" className="admin-refresh-btn" disabled={saving}>
            등록
          </button>
        </div>
      </form>

      {errorMsg && <p className="admin-error-text">{errorMsg}</p>}

      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>발행일</th>
              <th>교회</th>
              <th>구분</th>
              <th>금액</th>
              <th>사업자번호</th>
              <th>상태</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading ? (
              <tr>
                <td colSpan={7}>등록된 항목이 없습니다.</td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id}>
                  <td>{String(item.issuedAt).slice(0, 10)}</td>
                  <td>{item.authChurch}</td>
                  <td>{TYPE_LABEL[item.invoiceType] ?? item.invoiceType}</td>
                  <td>{item.amount.toLocaleString()}원</td>
                  <td>{item.businessNumber || '-'}</td>
                  <td>
                    <select value={item.status} onChange={(e) => handleStatusChange(item.id, e.target.value as InvoiceStatus)}>
                      {Object.entries(STATUS_LABEL).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button type="button" className="admin-btn delete-btn" onClick={() => handleDelete(item.id)}>
                      삭제
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
